import { apiRequest } from './api';

/**
 * World authoring API.
 *
 * Thin wrappers over `apiRequest`, the same shape as `services/npcs.js`.
 * Admin-only, enforced by `adminAuth` on the server — hiding the page from
 * non-admins is a rendering decision, not a security one.
 */

/** Summaries only. The full document carries three arrays of up to 250,000
 *  integers, which the list has no use for. */
export const listWorlds = (token) => apiRequest('/api/maps', { token });

/** One world in full, tile arrays included. */
export const getWorld = (token, name) => apiRequest(`/api/maps/${name}`, { token });

/**
 * What switching this world off would do, asked BEFORE doing it.
 *
 * Separate from the toggle so the confirmation can say "this will move 3
 * players and break 2 portals" rather than reporting it afterwards.
 */
export const getWorldImpact = (token, name) => apiRequest(`/api/maps/${name}/impact`, { token });

export const updateWorld = (token, name, changes) => apiRequest(`/api/maps/${name}`, {
  method: 'PATCH', token, body: changes,
});

/** A new world: bordered, floored, one spawn, and switched OFF. */
export const createWorld = (token, world) => apiRequest('/api/maps', {
  method: 'POST', token, body: world,
});

/**
 * Save without publishing.
 *
 * Writes `draft` on the document and changes nothing players can see. The split
 * is what makes a redeploy mid-edit safe — `toTiled()` never reads the draft.
 */
export const saveWorldDraft = (token, name, geometry) => apiRequest(`/api/maps/${name}`, {
  method: 'PUT', token, body: geometry,
});

/** Promote the draft, and rebuild any rooms running on the old shape. */
export const publishWorld = (token, name) => apiRequest(`/api/maps/${name}/publish`, {
  method: 'POST', token,
});

export const discardWorldDraft = (token, name) => apiRequest(`/api/maps/${name}/discard`, {
  method: 'POST', token,
});

/** Refused while the world is switched on — see the note on the route. */
export const deleteWorld = (token, name) => apiRequest(`/api/maps/${name}`, {
  method: 'DELETE', token,
});

/**
 * Copy a shipped `.tmj` into the database so it can be edited.
 *
 * Pass a name for one world, or nothing for every file-only world. Never
 * overwrites — a world already in the database has been edited, and replacing
 * it with the file it came from would throw that away. `npm run seed:maps
 * -- --force` is the deliberate, typed-on-purpose version of that.
 */
export const importWorlds = (token, name = null) => apiRequest('/api/maps/import', {
  method: 'POST', token, body: name ? { name } : { all: true },
});
